import React, { useState, useEffect } from 'react';
import { 
  MessageSquare, Plus, Search, Filter, Clock, CheckCircle2, 
  AlertCircle, Send, ShieldAlert, ArrowUpRight, Crown, Globe,
  Building2, Users, X, RefreshCw
} from 'lucide-react';
import { bdAPI } from '../utils/api';

const priorityColors = {
  Urgent: { bg: 'bg-rose-500/10', text: 'text-rose-400', border: 'border-rose-500/30' },
  High: { bg: 'bg-orange-500/10', text: 'text-orange-400', border: 'border-orange-500/30' },
  Medium: { bg: 'bg-indigo-500/10', text: 'text-indigo-400', border: 'border-indigo-500/30' },
  Low: { bg: 'bg-slate-500/10', text: 'text-slate-400', border: 'border-slate-500/30' }
};

const statusColors = {
  open: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', border: 'border-emerald-500/30' },
  pending: { bg: 'bg-amber-500/15', text: 'text-amber-400', border: 'border-amber-500/30' },
  resolved: { bg: 'bg-indigo-500/15', text: 'text-indigo-400', border: 'border-indigo-500/30' }
};

const BDSupport = () => {
  const [activeTab, setActiveTab] = useState('my'); // 'my' or 'team'
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  // Selected Ticket for Chat/Thread
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replyLoading, setReplyLoading] = useState(false);

  // New Ticket Modal State
  const [showNewModal, setShowNewModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    subject: '',
    category: 'Commission & Settle Issue',
    priority: 'High',
    message: '',
    contact: ''
  });

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const res = await bdAPI.getTickets({ scope: activeTab });
      if (res.data?.success) {
        setTickets(res.data.data || []);
        if (selectedTicket) {
          const updated = (res.data.data || []).find(t => t.id === selectedTicket.id);
          setSelectedTicket(updated || null);
        }
      }
    } catch (err) {
      console.error("Failed to fetch BD tickets:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [activeTab]);

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.message.trim()) {
      alert("Please provide both subject and message.");
      return;
    }

    try {
      setSubmitting(true);
      const res = await bdAPI.createTicket(form);
      if (res.data?.success) {
        setShowNewModal(false);
        setForm({
          subject: '',
          category: 'Commission & Settle Issue',
          priority: 'High',
          message: '',
          contact: ''
        });
        setActiveTab('my');
        await fetchTickets();
      }
    } catch (err) {
      console.error("Error creating ticket:", err);
      alert(err.response?.data?.message || "Failed to create support ticket.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedTicket) return;
    try {
      setReplyLoading(true);
      const res = await bdAPI.replyTicket(selectedTicket.id, { message: replyText });
      if (res.data?.success) {
        setReplyText('');
        await fetchTickets();
      }
    } catch (err) {
      console.error("Failed to send reply:", err);
      alert(err.response?.data?.message || "Could not send reply");
    } finally {
      setReplyLoading(false);
    }
  };

  const filteredTickets = tickets.filter(t => {
    const matchesSearch = 
      (t.subject || '').toLowerCase().includes(search.toLowerCase()) ||
      (t.ticket_id || '').toLowerCase().includes(search.toLowerCase()) ||
      (t.user_id || '').toString().includes(search);
    const matchesStatus = statusFilter === 'ALL' || t.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-gradient-to-r from-slate-900 via-indigo-950/40 to-slate-900 p-6 rounded-2xl border border-indigo-500/20 shadow-xl backdrop-blur-md">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 flex items-center gap-1.5">
              <ShieldAlert className="w-3.5 h-3.5" />
              BD Support & Grievance Desk
            </span>
          </div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            Help & Complaints Center
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            Submit escalations to Manager / Super Admin and monitor support tickets from your agencies and hosts.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => fetchTickets()}
            disabled={loading}
            className="p-2.5 rounded-xl bg-slate-800/80 hover:bg-slate-700/80 text-slate-300 border border-slate-700/60 transition-all flex items-center gap-2 text-sm"
            title="Refresh Tickets"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-indigo-400' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => setShowNewModal(true)}
            className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 text-white font-medium shadow-lg shadow-indigo-600/20 flex items-center gap-2 transition-all transform active:scale-95 text-sm"
          >
            <Plus className="w-4 h-4" />
            New Complaint / Escalation
          </button>
        </div>
      </div>

      {/* Tabs & Filters */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        {/* Navigation Tabs */}
        <div className="flex p-1 bg-slate-900/80 rounded-xl border border-slate-800/80 w-full sm:w-auto">
          <button
            onClick={() => { setActiveTab('my'); setSelectedTicket(null); }}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
              activeTab === 'my'
                ? 'bg-gradient-to-r from-indigo-600 to-violet-600 text-white shadow-md'
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <Crown className="w-4 h-4" />
            My Complaints to Manager/Admin
            <span className="ml-1.5 px-2 py-0.5 text-xs rounded-full bg-white/20">
              {activeTab === 'my' ? tickets.length : ''}
            </span>
          </button>

          <button
            onClick={() => { setActiveTab('team'); setSelectedTicket(null); }}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
              activeTab === 'team'
                ? 'bg-gradient-to-r from-indigo-600 to-violet-600 text-white shadow-md'
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <Building2 className="w-4 h-4" />
            Agencies & Hosts Tickets
            <span className="ml-1.5 px-2 py-0.5 text-xs rounded-full bg-white/20">
              {activeTab === 'team' ? tickets.length : ''}
            </span>
          </button>
        </div>

        {/* Search & Status Filters */}
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search subject, ID, or user..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-slate-900/90 border border-slate-800 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500 transition-all"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 bg-slate-900/90 border border-slate-800 rounded-xl text-sm text-slate-300 focus:outline-none focus:border-indigo-500 transition-all"
          >
            <option value="ALL">All Status</option>
            <option value="open">Open</option>
            <option value="pending">Pending</option>
            <option value="resolved">Resolved</option>
          </select>
        </div>
      </div>

      {/* Main Grid: Ticket List + Conversation Thread */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Side: Ticket Cards */}
        <div className={`space-y-3 ${selectedTicket ? 'lg:col-span-5' : 'lg:col-span-12'}`}>
          {loading ? (
            <div className="p-12 text-center text-slate-500 bg-slate-900/40 rounded-2xl border border-slate-800/80">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-indigo-400 mb-3" />
              Loading tickets...
            </div>
          ) : filteredTickets.length === 0 ? (
            <div className="p-12 text-center text-slate-400 bg-slate-900/40 rounded-2xl border border-slate-800/80">
              <MessageSquare className="w-12 h-12 mx-auto text-slate-600 mb-3" />
              <h3 className="text-base font-semibold text-slate-300">No Tickets Found</h3>
              <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
                {activeTab === 'my' 
                  ? "You have not raised any complaints yet. Click 'New Complaint' if you need assistance from the Manager or Admin."
                  : "No agency or host complaints currently logged in your network."}
              </p>
            </div>
          ) : (
            filteredTickets.map(ticket => {
              const pStyle = priorityColors[ticket.priority] || priorityColors.Medium;
              const sStyle = statusColors[ticket.status] || statusColors.open;
              const isSelected = selectedTicket?.id === ticket.id;

              return (
                <div
                  key={ticket.id}
                  onClick={() => setSelectedTicket(ticket)}
                  className={`p-4 rounded-xl border transition-all cursor-pointer relative ${
                    isSelected 
                      ? 'bg-slate-800/90 border-indigo-500/80 shadow-lg shadow-indigo-950/40' 
                      : 'bg-slate-900/60 hover:bg-slate-800/50 border-slate-800/80 hover:border-slate-700'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-mono text-indigo-400 bg-indigo-500/10 px-2 py-0.5 rounded border border-indigo-500/20">
                        #{ticket.ticket_id || ticket.id}
                      </span>
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${pStyle.bg} ${pStyle.text} ${pStyle.border}`}>
                        {ticket.priority || 'Medium'}
                      </span>
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${sStyle.bg} ${sStyle.text} ${sStyle.border}`}>
                        {ticket.status?.toUpperCase() || 'OPEN'}
                      </span>
                      <span className="text-[11px] text-slate-400 bg-slate-800 px-2 py-0.5 rounded">
                        {ticket.user_type || 'BD'}
                      </span>
                    </div>
                    <span className="text-[11px] text-slate-500 whitespace-nowrap">
                      {new Date(ticket.created_at).toLocaleDateString()}
                    </span>
                  </div>

                  <h3 className="text-sm font-semibold text-white truncate mb-1">
                    {ticket.subject}
                  </h3>
                  <p className="text-xs text-slate-400 line-clamp-2 leading-relaxed">
                    {ticket.message}
                  </p>

                  <div className="flex items-center justify-between text-[11px] text-slate-500 mt-3 pt-2.5 border-t border-slate-800/60">
                    <span className="truncate max-w-[180px]">
                      {ticket.category || 'General'}
                    </span>
                    <span className="flex items-center gap-1 text-indigo-400 font-medium">
                      {(ticket.replies || []).length} Replies
                      <ArrowUpRight className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Right Side: Conversation Thread */}
        {selectedTicket && (
          <div className="lg:col-span-7 bg-slate-900/90 border border-slate-800 rounded-2xl p-5 flex flex-col h-[700px] shadow-2xl backdrop-blur-md">
            {/* Header of Conversation */}
            <div className="flex items-start justify-between pb-4 border-b border-slate-800 gap-3">
              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-xs font-mono text-indigo-400 bg-indigo-500/10 px-2 py-0.5 rounded border border-indigo-500/20">
                    #{selectedTicket.ticket_id || selectedTicket.id}
                  </span>
                  <span className="text-xs px-2.5 py-0.5 rounded bg-slate-800 text-slate-300 font-medium">
                    {selectedTicket.category || 'General'}
                  </span>
                </div>
                <h2 className="text-base font-bold text-white">
                  {selectedTicket.subject}
                </h2>
                <div className="text-xs text-slate-400 mt-1 flex items-center gap-3">
                  <span>From: <strong className="text-slate-200">{selectedTicket.user_name || `User #${selectedTicket.user_id}`}</strong> ({selectedTicket.user_type})</span>
                  <span>•</span>
                  <span>{new Date(selectedTicket.created_at).toLocaleString()}</span>
                </div>
              </div>

              <button
                onClick={() => setSelectedTicket(null)}
                className="p-1.5 rounded-lg bg-slate-800 text-slate-400 hover:text-white transition-all"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Conversation Messages */}
            <div className="flex-1 overflow-y-auto py-4 space-y-4 pr-1">
              {/* Original Message */}
              <div className="bg-slate-800/60 border border-slate-700/60 rounded-xl p-4">
                <div className="flex items-center justify-between text-xs text-slate-400 mb-2">
                  <span className="font-semibold text-indigo-400 flex items-center gap-1.5">
                    <Crown className="w-3.5 h-3.5" />
                    Original Complaint
                  </span>
                  <span>{new Date(selectedTicket.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
                <p className="text-sm text-slate-200 leading-relaxed whitespace-pre-wrap">
                  {selectedTicket.message}
                </p>
              </div>

              {/* Replies */}
              {(selectedTicket.replies || []).map((reply, index) => {
                const isAdmin = reply.sender_type === 'ADMIN' || reply.role === 'ADMIN';
                const isManager = reply.sender_type === 'MANAGER' || reply.role === 'MANAGER';
                const isBD = reply.sender_type === 'BD' || reply.role === 'BD';

                return (
                  <div
                    key={index}
                    className={`p-4 rounded-xl border max-w-[85%] ${
                      isAdmin
                        ? 'ml-auto bg-gradient-to-br from-indigo-950/70 to-purple-950/70 border-indigo-500/40 text-right'
                        : isManager
                        ? 'ml-auto bg-gradient-to-br from-emerald-950/70 to-teal-950/70 border-emerald-500/40 text-right'
                        : isBD
                        ? 'ml-auto bg-gradient-to-br from-slate-800 to-indigo-950/40 border-slate-700 text-right'
                        : 'mr-auto bg-slate-800/80 border-slate-700/80 text-left'
                    }`}
                  >
                    <div className={`flex items-center gap-2 text-xs mb-1.5 ${isAdmin || isManager || isBD ? 'justify-end' : 'justify-start'}`}>
                      <span className={`font-semibold px-2 py-0.5 rounded text-[10px] ${
                        isAdmin 
                          ? 'bg-rose-500/20 text-rose-300 border border-rose-500/30'
                          : isManager
                          ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                          : 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30'
                      }`}>
                        {reply.sender_type || (isAdmin ? 'SUPER ADMIN' : isManager ? 'MANAGER' : 'REPLY')}
                      </span>
                      <span className="text-slate-400 text-[11px]">
                        {reply.timestamp ? new Date(reply.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                      </span>
                    </div>
                    <p className="text-sm text-slate-200 leading-relaxed whitespace-pre-wrap">
                      {reply.message}
                    </p>
                  </div>
                );
              })}
            </div>

            {/* Reply Input Box */}
            <div className="pt-3 border-t border-slate-800 flex items-center gap-2">
              <input
                type="text"
                placeholder="Write your response or update here..."
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendReply();
                  }
                }}
                className="flex-1 bg-slate-800/90 border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-slate-400 focus:outline-none focus:border-indigo-500 transition-all"
              />
              <button
                onClick={handleSendReply}
                disabled={replyLoading || !replyText.trim()}
                className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 text-white font-medium flex items-center gap-2 text-sm disabled:opacity-50 transition-all shadow-md"
              >
                {replyLoading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal: New Complaint / Escalation */}
      {showNewModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl max-w-lg w-full p-6 shadow-2xl">
            <div className="flex items-center justify-between pb-4 border-b border-slate-800">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-indigo-500/10 rounded-lg border border-indigo-500/20 text-indigo-400">
                  <ShieldAlert className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-white">Create Escalation Ticket</h3>
                  <p className="text-xs text-slate-400">Directly routed to Executive Manager & Super Admin</p>
                </div>
              </div>
              <button
                onClick={() => setShowNewModal(false)}
                className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateTicket} className="space-y-4 mt-5">
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">Subject / Issue Summary *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Target Commission discrepancy for agency #401"
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  className="w-full bg-slate-800/90 border border-slate-700 rounded-xl px-3.5 py-2.5 text-sm text-white focus:outline-none focus:border-indigo-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">Category</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full bg-slate-800/90 border border-slate-700 rounded-xl px-3 py-2 text-sm text-slate-300 focus:outline-none focus:border-indigo-500"
                  >
                    <option value="Commission & Settle Issue">Commission & Settle Issue</option>
                    <option value="Agency Transfer / Onboarding">Agency Transfer / Onboarding</option>
                    <option value="Host Timing & Days Dispute">Host Timing & Days Dispute</option>
                    <option value="Policy Violation / Ban Appeal">Policy Violation / Ban Appeal</option>
                    <option value="Technical Glitch">Technical Glitch</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">Priority</label>
                  <select
                    value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: e.target.value })}
                    className="w-full bg-slate-800/90 border border-slate-700 rounded-xl px-3 py-2 text-sm text-slate-300 focus:outline-none focus:border-indigo-500"
                  >
                    <option value="Urgent">Urgent (Immediate)</option>
                    <option value="High">High</option>
                    <option value="Medium">Medium</option>
                    <option value="Low">Low</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">Detailed Message / Description *</label>
                <textarea
                  rows={4}
                  required
                  placeholder="Provide complete facts, agency ID, host ID, affected dates, or diamond count..."
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full bg-slate-800/90 border border-slate-700 rounded-xl p-3 text-sm text-white focus:outline-none focus:border-indigo-500 resize-none"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => setShowNewModal(false)}
                  className="px-4 py-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 text-sm font-medium transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 text-white font-medium text-sm transition-all shadow-lg shadow-indigo-600/20 disabled:opacity-50 flex items-center gap-2"
                >
                  {submitting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Submit Escalation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BDSupport;
